// 桌面壳(spec 260508-folder-feature M4 + M5)。
//
// 与 M3 之前的差异:
//   - 不再读 iconsStore 的 {row,col} 坐标(P6 删除该 store)。
//   - 渲染来源从 registry.entries 切到 useDesktopLayoutStore.layout.items;
//     items[type=app] → DesktopAppIcon, items[type=folder] → FolderIcon。
//   - 挂载时调 load() 拉取布局;status='loading' 显示加载占位。
//   - 不在卸载时强制 flush:debounce + 重试机制已经覆盖大多数场景,卸载强
//     flush 反而在路由切换 / 测试 teardown 中产生竞态(plan §4.2)。
//
// M5 拖拽 dispatcher:
//   - DesktopAppIcon / FolderIcon 把 (DragEvent, DragPayload) emit 给
//     ``handleDesktopDragEnd``;由 dispatcher 用 findDropTarget 决定调用哪
//     个 helper。
//   - 弹层内 App 的 onDragEnd 由 FolderPopover 单独 emit 给
//     ``handleFolderItemDragEnd``,因为它的源是 'folder' 而不是 'desktop'。

import { useCallback, useEffect, useRef, useState } from 'react';
import type { DragEvent, MouseEvent as ReactMouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { useRegistry } from '../apps/registryContext';
import { useDesktopLayoutStore } from '../store/desktopLayoutStore';
import { pushNotification } from '../store/notificationStore';
import { DesktopAppIcon } from './DesktopAppIcon';
import {
  DesktopContextMenu,
  type ContextMenuState,
} from './DesktopContextMenu';
import { findDropTarget, type DragPayload } from './desktopDnD';
import {
  createFolderWithApp,
  joinFolder,
  mergeAppsToFolder,
  moveOutOfFolder,
  reorderApp,
  reorderFolder,
} from './desktopDnDActions';
import { FolderIcon } from './FolderIcon';
import { FolderPopover } from './FolderPopover';
import { ServiceStatusCards } from './ServiceStatusCards';
import { WidgetHost } from './widgets/WidgetHost';

interface DesktopProps {
  onLaunch: (appId: string) => void;
}

export function Desktop({ onLaunch }: DesktopProps) {
  const { t } = useTranslation();
  const registry = useRegistry();
  const status = useDesktopLayoutStore((s) => s.status);
  const layout = useDesktopLayoutStore((s) => s.layout);
  const openFolderId = useDesktopLayoutStore((s) => s.openFolderId);
  const setOpenFolder = useDesktopLayoutStore((s) => s.setOpenFolder);
  const load = useDesktopLayoutStore((s) => s.load);
  const applyLocalChange = useDesktopLayoutStore((s) => s.applyLocalChange);


  // 触发 → 文件夹按钮的反向引用,用于 ESC 关闭后焦点归位。
  const triggerRef = useRef<HTMLElement | null>(null);

  // 最近一次 dragover 的光标位置。
  //
  // 为什么不能直接用 dragend 事件自带的 clientX/clientY:HTML5 DnD 里 dragend
  // 派发在**拖拽源**上,浏览器给的坐标不保证是落点 —— 实测把图标拖到第 8 列,
  // dragend 报的仍是它自己原来的格子,于是"拖到空白处重排"看起来毫无反应
  // (算出来的落点索引就是它自己的位置)。dragover 则是在**当前悬停的元素**上
  // 连续派发,坐标准确,所以这里持续记录,dragend 时优先用它。
  // 右键菜单(唯一入口:App 图标上右键 →「移入新建文件夹」)。
  const [menu, setMenu] = useState<ContextMenuState | null>(null);
  const closeMenu = useCallback(() => setMenu(null), []);
  const handleAppContextMenu = useCallback(
    (e: ReactMouseEvent, appId: string) => {
      e.preventDefault();
      setMenu({ x: e.clientX, y: e.clientY, appId });
    },
    [],
  );
  // 桌面壳内一律吃掉浏览器原生右键菜单 —— 这是个"桌面",弹出浏览器的
  // 「返回/重新加载/另存为」很出戏。App 图标上的 handler 先跑并把自己的菜单
  // 立起来(事件冒泡到这里时 menu 已设好);其余区域(空白、文件夹图标、任务栏)
  // 只拦截不给菜单,顺手把已打开的菜单关掉。
  const handleShellContextMenu = useCallback(
    (e: ReactMouseEvent) => {
      e.preventDefault();
      const onApp = (e.target as HTMLElement | null)?.closest?.(
        '.os-desktop-grid [data-app-id]',
      );
      if (!onApp) setMenu(null);
    },
    [],
  );
  const handleCreateFolder = useCallback(
    (appId: string) => {
      let created = '';
      applyLocalChange((draft) => {
        created = createFolderWithApp(draft, appId, t('desktop.folder.newName'));
      });
      // 建完直接打开,用户可以立刻改名 / 确认内容。单成员文件夹现在也能打开
      // (见 FolderPopover 自动关闭条件的注释)。
      if (created) {
        triggerRef.current = null;
        setOpenFolder(created);
      }
    },
    [applyLocalChange, setOpenFolder, t],
  );

  const lastDragPos = useRef<{ x: number; y: number } | null>(null);
  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    // 必须 preventDefault,否则浏览器认为整个桌面不接受 drop:光标一路显示
    // "禁止",而且 FolderIcon 之外的区域根本不会派发 dragover。
    e.preventDefault();
    lastDragPos.current = { x: e.clientX, y: e.clientY };
  }, []);

  // 失败提示由 store 统一 pushNotification,这里吞掉 rejection 即可 —— 不吞
  // 会在 StrictMode / 首屏失败时留下 unhandled rejection 噪音。
  const retryLoad = useCallback(() => {
    void load().catch(() => {});
  }, [load]);

  useEffect(() => {
    if (status === 'idle') {
      retryLoad();
    }
  }, [status, retryLoad]);

  // -------------------------------------------------------------------------
  // 拖拽 dispatcher
  // -------------------------------------------------------------------------
  //
  // 桌面 item(App / Folder)的 onDragEnd:
  //   - source='desktop', itemType='app':
  //       drop on app/folder/blank → merge / join / reorder
  //   - source='desktop', itemType='folder':
  //       drop on folder → 拒绝(嵌套);drop on app → 拒绝;drop on blank → reorder
  //
  // 弹层内 App 的 onDragEnd 由 FolderPopover 转发给同一 dispatcher,但 source
  // 是 'folder',分流到 moveOutOfFolder 路径。
  const handleDesktopDragEnd = useCallback(
    (event: DragEvent<HTMLButtonElement>, payload: DragPayload) => {
      const { layout: current } = useDesktopLayoutStore.getState();
      if (!current) return;
      // 优先用 dragover 记录的落点(见 lastDragPos 注释);没记录到再退回事件自带
      // 坐标。用完即清,避免下一次拖拽复用上一次的陈旧位置。
      const pos = lastDragPos.current ?? {
        x: event.clientX,
        y: event.clientY,
      };
      lastDragPos.current = null;
      const target = findDropTarget(pos.x, pos.y, current);

      // ---------- source = desktop / app ----------
      if (payload.source === 'desktop' && payload.itemType === 'app') {
        if (target.kind === 'app') {
          if (target.appId === payload.appId) return; // 自己拖到自己 noop
          // 合并:在 target.appId 占位创建新 folder [target, source]
          let createdFolderId = '';
          applyLocalChange((draft) => {
            createdFolderId = mergeAppsToFolder(
              draft,
              target.appId,
              payload.appId,
              t('desktop.folder.newName'),
            );
          });
          if (createdFolderId) {
            // 自动打开新建的文件夹弹层(triggerRef 留空 — ESC 焦点回 body)。
            triggerRef.current = null;
            setOpenFolder(createdFolderId);
          }
          return;
        }
        if (target.kind === 'folder') {
          applyLocalChange((draft) => {
            joinFolder(draft, target.folderId, payload.appId);
          });
          return;
        }
        if (target.kind === 'blank') {
          applyLocalChange((draft) => {
            reorderApp(draft, payload.appId, target.index);
          });
          return;
        }
        // popover-blank / none → noop
        return;
      }

      // ---------- source = desktop / folder ----------
      if (payload.source === 'desktop' && payload.itemType === 'folder') {
        if (target.kind === 'folder' || target.kind === 'app') {
          // 文件夹不能嵌套也不能反向合并到 app。
          pushNotification({
            level: 'danger',
            message: t('desktop.folder.cannotNest'),
            key: 'desktop-folder-cannot-nest',
          });
          return;
        }
        if (target.kind === 'blank') {
          applyLocalChange((draft) => {
            reorderFolder(draft, payload.folderId, target.index);
          });
        }
        return;
      }

      // ---------- source = folder(弹层内 App) ----------
      // 这一支理论上由 FolderPopover.onDragEnd 转发到这里。
      if (payload.source === 'folder') {
        if (target.kind === 'blank') {
          const outcomeRef: { value: 'dissolved' | 'kept' | 'noop' } = {
            value: 'noop',
          };
          applyLocalChange((draft) => {
            outcomeRef.value = moveOutOfFolder(
              draft,
              payload.folderId,
              payload.appId,
              target.index,
            );
          });
          if (outcomeRef.value === 'dissolved') {
            setOpenFolder(null);
          }
          return;
        }
        // 落到桌面 App / Folder / 弹层内任意位置 → 拒绝
        pushNotification({
          level: 'warning',
          message: t('desktop.folder.dragToBlankArea'),
          key: 'desktop-folder-drag-to-blank',
        });
      }
    },
    [applyLocalChange, setOpenFolder, t],
  );

  if (!layout) {
    return (
      <div
        role="grid"
        aria-label="desktop"
        data-testid="desktop"
        className="absolute inset-0"
      >
        <WidgetHost />
        <div className="os-desktop-grid" data-testid="desktop-grid">
          {status === 'loading' && (
            <div
              role="status"
              aria-live="polite"
              className="os-desktop-loading"
            >
              加载桌面布局…
            </div>
          )}
          {status === 'error' && (
            <div
              role="alert"
              className="os-desktop-loading os-desktop-loading--error"
            >
              加载桌面布局失败
              <button
                type="button"
                className="os-desktop-loading__retry"
                onClick={retryLoad}
              >
                重试
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  const openFolder =
    openFolderId === null
      ? null
      : layout.folders.find((f) => f.id === openFolderId) ?? null;

  return (
    <div
      role="grid"
      aria-label="desktop"
      data-testid="desktop"
      className="absolute inset-0"
      onDragOver={handleDragOver}
      onContextMenu={handleShellContextMenu}
    >
      {/* <ServiceStatusCards onLaunch={onLaunch} /> */}
      <WidgetHost />
      <div className="os-desktop-grid" data-testid="desktop-grid">
        {layout.items.map((item, idx) => {
          if (item.type === 'app') {
            const entry = registry.get(item.appId);
            if (!entry) return null; // 防御:registry 与 layout 暂时不一致
            return (
              <DesktopAppIcon
                key={`app:${item.appId}`}
                entry={entry}
                fallbackIndex={idx}
                onActivate={() => onLaunch(item.appId)}
                dragSource="desktop"
                onDragEnd={handleDesktopDragEnd}
                onContextMenu={(e) => handleAppContextMenu(e, item.appId)}
              />
            );
          }
          // type === 'folder'
          const folder = layout.folders.find((f) => f.id === item.folderId);
          if (!folder) return null;
          return (
            <FolderIcon
              key={`folder:${item.folderId}`}
              folder={folder}
              registry={registry}
              fallbackIndex={idx}
              onActivate={(folderId) => {
                // 记下触发按钮以便 ESC / 关闭后焦点回归
                const node = document.querySelector<HTMLElement>(
                  `[data-folder-id="${folderId}"]`,
                );
                triggerRef.current = node;
                setOpenFolder(folderId);
              }}
              onDragEnd={handleDesktopDragEnd}
            />
          );
        })}
      </div>
      <FolderPopover
        folder={openFolder}
        registry={registry}
        onLaunch={onLaunch}
        triggerRef={triggerRef}
        onMemberDragEnd={handleDesktopDragEnd}
      />
      {menu && (
        <DesktopContextMenu
          state={menu}
          label={t('desktop.folder.createFromApp')}
          onSelect={handleCreateFolder}
          onClose={closeMenu}
        />
      )}
    </div>
  );
}
